import React from "react";
import { Home as HomeIcon } from "lucide-react";
import { useI18n } from "@/lib/i18n";

// Side option canonical values stored in DB / submitted to backend.
// Display labels go through i18n.
export const SIDE_OPTIONS = [
  { value: "Road",              labelKey: "compass.side.road" },
  { value: "Another Plot",      labelKey: "compass.side.anotherPlot" },
  { value: "Open Land",         labelKey: "compass.side.openLand" },
  { value: "Pond / Water Body", labelKey: "compass.side.pond" },
  { value: "Nala / Drain",      labelKey: "compass.side.nala" },
  { value: "Building",          labelKey: "compass.side.building" },
  { value: "Garden / Farm",     labelKey: "compass.side.garden" },
  { value: "Other",             labelKey: "compass.side.other" },
];

const SIDES = [
  { key: "north", labelKey: "facing.N", abbr: "N" },
  { key: "east",  labelKey: "facing.E", abbr: "E" },
  { key: "south", labelKey: "facing.S", abbr: "S" },
  { key: "west",  labelKey: "facing.W", abbr: "W" },
];

export default function PlotCompass({ value, onChange, testidPrefix = "compass", showDimensions = false }) {
  const { t } = useI18n();
  const set = (sideKey, v) => onChange({ ...value, [`${sideKey}Side`]: v });
  const setLen = (sideKey, v) => onChange({ ...value, [`${sideKey}Length`]: v });

  const Selector = ({ side }) => (
    <div className="flex flex-col items-center gap-1 min-w-0 w-full">
      <span className="text-[10px] font-semibold tracking-wider text-emerald-700 uppercase">
        {t(side.labelKey)} <span className="text-slate-400 font-normal">({side.abbr})</span>
      </span>
      <select
        className="nc-input text-xs py-1.5 px-2 w-full"
        value={value[`${side.key}Side`] || ""}
        onChange={(e) => set(side.key, e.target.value)}
        data-testid={`${testidPrefix}-${side.key}-select`}
      >
        <option value="">{t("compass.select")}</option>
        {SIDE_OPTIONS.map((opt) => (
          <option key={opt.value} value={opt.value}>{t(opt.labelKey)}</option>
        ))}
      </select>
      {showDimensions && (
        <input
          type="number"
          min="0"
          placeholder="Length (ft)"
          className="nc-input text-xs py-1.5 px-2 w-full"
          value={value[`${side.key}Length`] || ""}
          onChange={(e) => setLen(side.key, e.target.value)}
          data-testid={`${testidPrefix}-${side.key}-length`}
        />
      )}
    </div>
  );

  return (
    <div
      className="grid gap-3 items-center max-w-sm mx-auto"
      style={{
        gridTemplateColumns: "1fr 1.2fr 1fr",
        gridTemplateAreas: '"top-l n top-r" "w plot e" "bot-l s bot-r"',
      }}
      data-testid={`${testidPrefix}-root`}
    >
      <div style={{ gridArea: "n" }}><Selector side={SIDES[0]} /></div>
      <div style={{ gridArea: "w" }}><Selector side={SIDES[3]} /></div>

      <div
        style={{ gridArea: "plot" }}
        className="aspect-square rounded-xl border-2 border-dashed border-emerald-400 bg-gradient-to-br from-emerald-50 to-sky-50 flex flex-col items-center justify-center text-emerald-700 relative"
      >
        <HomeIcon className="w-7 h-7" />
        <span className="text-[10px] font-semibold uppercase tracking-wider mt-1">{t("compass.yourPlot")}</span>
        <span className="absolute -top-1.5 left-1/2 -translate-x-1/2 text-[10px] font-bold text-emerald-700 bg-white px-1 rounded">N</span>
        <span className="absolute -bottom-1.5 left-1/2 -translate-x-1/2 text-[10px] font-bold text-emerald-700 bg-white px-1 rounded">S</span>
        <span className="absolute -left-2 top-1/2 -translate-y-1/2 text-[10px] font-bold text-emerald-700 bg-white px-1 rounded">W</span>
        <span className="absolute -right-2 top-1/2 -translate-y-1/2 text-[10px] font-bold text-emerald-700 bg-white px-1 rounded">E</span>
      </div>

      <div style={{ gridArea: "e" }}><Selector side={SIDES[1]} /></div>
      <div style={{ gridArea: "s" }}><Selector side={SIDES[2]} /></div>
    </div>
  );
}

export function emptySides() {
  return { northSide: "", southSide: "", eastSide: "", westSide: "" };
}

export function emptySideLengths() {
  return { northLength: "", southLength: "", eastLength: "", westLength: "" };
}

export function sidesToUrlParams(sides) {
  const out = {};
  if (sides.northSide) out.north_side = sides.northSide;
  if (sides.southSide) out.south_side = sides.southSide;
  if (sides.eastSide)  out.east_side  = sides.eastSide;
  if (sides.westSide)  out.west_side  = sides.westSide;
  return out;
}

export function sidesFromUrlParams(params) {
  return {
    northSide: params.get("north_side") || "",
    southSide: params.get("south_side") || "",
    eastSide:  params.get("east_side")  || "",
    westSide:  params.get("west_side")  || "",
  };
}
